import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useCatalog, filterFields, coveredTriggers, nameableTriggers } from './catalog'
import { SchemaForm, validateSchema } from './schema-form'
import { MappingForm } from './mapping-form'
import { createTarget, listTargets, previewRoute, testRoute } from './alert-routing-api'
import { serverMessage } from '../../../../shared/sso-workspace/format'
import { OP_WORDS, categoryNoun } from './route-summary'
import { effectiveMapping, renderTemplate, sampleContext } from './render-template'
import { SecretPanel } from './secret-panel'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'

const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']]
const DIGESTS = [5, 10, 15, 30, 60, 120, 240, 1440]
const TARGETS_TAB = '/admin/alert-routing?tab=targets'

const viewerZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch (e) {
    return 'UTC'
  }
}

const hasEmailList = at => Boolean(at && (at.configSchema || []).some(f => f.type === 'email_list'))

export const TriggerStep = ({ route, set, problems = [] }) => {
  const catalog = useCatalog()
  // an organization's route never offers a platform-only alert (catalog.js)
  const triggers = nameableTriggers(route, catalog)
  // what "every alert" leaves out: the platform-only ones a platform route may name
  const leftOut = triggers.filter(t => t.platformOnly)
  const choosing = Boolean(route.choosing)
  const toggle = kind => set({ choosing: true, triggerKinds: route.triggerKinds.includes(kind) ? route.triggerKinds.filter(k => k !== kind) : [...route.triggerKinds, kind] })
  const noun = categoryNoun(route.category, catalog)
  return (
    <>
      <label className="sw-label" htmlFor="ar-cat">Category</label>
      <select id="ar-cat" className="form-select" value={route.category} onChange={e => set({ category: e.target.value, triggerKinds: [], filters: [], choosing: false })}>
        {catalog.categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
      <div className="form-check mt-2">
        <input id="ar-wild" className="form-check-input" type="radio" checked={!choosing} onChange={() => set({ triggerKinds: [], choosing: false })}
          aria-describedby={leftOut.length ? 'ar-wild-hint' : undefined} />
        <label className="form-check-label" htmlFor="ar-wild">Every {noun} alert (including future ones)</label>
        {leftOut.length > 0 &&
          <p className="sw-hint m-0" id="ar-wild-hint">
            Leaves out {leftOut.map(t => t.label).join(', ')}: choose {leftOut.length === 1 ? 'it' : 'them'} under "Only these alerts".
          </p>}
      </div>
      <div className="form-check">
        <input id="ar-set" className="form-check-input" type="radio" checked={choosing} onChange={() => set({ choosing: true })} />
        <label className="form-check-label" htmlFor="ar-set">Only these alerts</label>
      </div>
      {choosing && triggers.map(t => (
        <div className="form-check ms-4" key={t.kind}>
          <input id={`ar-t-${t.kind}`} className="form-check-input" type="checkbox" checked={route.triggerKinds.includes(t.kind)} onChange={() => toggle(t.kind)} />
          <label className="form-check-label" htmlFor={`ar-t-${t.kind}`}>{t.label} <span className="sw-hint">({t.severity}) {t.description}</span></label>
        </div>
      ))}
      {problems.map(p => <p key={p} className="text-danger small mt-1" role="alert">{p}</p>)}
      <details className="mt-2"><summary>What these alerts carry</summary>
        <ul>{[...new Map(triggers.filter(t => (choosing ? route.triggerKinds.includes(t.kind) : !t.platformOnly)).flatMap(t => t.schema).map(f => [f.key, f])).values()]
          .map(f => <li key={f.key}><code>{f.key}</code> {f.label} <span className="sw-hint">{f.type}{f.class === 'personal' ? ' · personal' : ''}</span></li>)}</ul>
      </details>
    </>
  )
}

// The value a filter starts with for its field and operator, so what is
// shown is what is sent: "exists" and yes/no fields start at yes (true).
export const defaultFilterValue = (field, op) => {
  if (op === 'exists' || (field && field.type === 'bool')) return true
  if (op === 'in' || op === 'not_in') return []
  return ''
}

// Times are shown and entered in the viewer's local time and sent as UTC.
const pad = n => String(n).padStart(2, '0')
export const toLocalInput = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
export const fromLocalInput = v => {
  if (!v) return ''
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString()
}

// "is one of" values: a comma ends a value, which becomes a chip; the text
// after the last comma stays in the box (and counts as a value) until then.
const ListValue = ({ id, label, value, onChange }) => {
  const chips = Array.isArray(value) ? value : []
  const [text, setText] = useState('')
  const [done, setDone] = useState(chips)
  const emit = (list, pending) => onChange(pending.trim() ? [...list, pending.trim()] : list)
  const type = raw => {
    const parts = raw.split(',')
    const pending = parts.pop()
    const list = [...done, ...parts.map(x => x.trim()).filter(Boolean)]
    setDone(list)
    setText(pending)
    emit(list, pending)
  }
  const remove = i => {
    const list = done.filter((_, j) => j !== i)
    setDone(list)
    emit(list, text)
  }
  return (
    <div className="ar-chips">
      <ul className="ar-chip-list" aria-label={`${label} values`}>
        {done.map((v, i) => (
          <li key={`${v}-${i}`} className="ar-chip">{v}
            <button type="button" className="btn btn-link btn-sm p-0 ms-1" aria-label={`Remove ${v}`} onClick={() => remove(i)}>×</button>
          </li>
        ))}
      </ul>
      <input id={id} aria-label={label} className="form-control" placeholder="Type a value, then a comma" value={text} onChange={e => type(e.target.value)} />
    </div>
  )
}

const ValueInput = ({ id, label, field, op, value, onChange }) => {
  if (op === 'exists' || field.type === 'bool') {
    return <select id={id} aria-label={label} className="form-select" value={String(value === true || value === 'true')} onChange={e => onChange(e.target.value === 'true')}>
      <option value="true">yes</option><option value="false">no</option></select>
  }
  if (op === 'in' || op === 'not_in') return <ListValue id={id} label={label} value={value} onChange={onChange} />
  if (field.type === 'enum') {
    return <select id={id} aria-label={label} className="form-select" value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">Pick…</option>{field.enum.map(o => <option key={o} value={o}>{o}</option>)}</select>
  }
  if (field.type === 'int') return <input id={id} aria-label={label} type="number" className="form-control" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
  if (field.type === 'time') return <input id={id} aria-label={label} type="datetime-local" className="form-control" value={toLocalInput(value)} onChange={e => onChange(fromLocalInput(e.target.value))} />
  return <input id={id} aria-label={label} className="form-control" value={value || ''} onChange={e => onChange(e.target.value)} />
}

export const FiltersStep = ({ route, set }) => {
  const catalog = useCatalog()
  const fields = filterFields(route, catalog)
  const cat = catalog.categories.find(c => c.key === route.category) || { operators: {} }
  const opsFor = field => (cat.operators || {})[field.type] || ['equals']
  const setRow = (i, patch) => set({ filters: route.filters.map((f, j) => (j === i ? { ...f, ...patch } : f)) })
  const fieldOf = key => fields.find(x => x.key === key) || fields[0] || { type: 'string' }
  return (
    <>
      <label className="sw-label" htmlFor="ar-sev">Minimum severity</label>
      <select id="ar-sev" className="form-select" value={route.minSeverity} onChange={e => set({ minSeverity: e.target.value })}>
        <option value="info">info</option><option value="warning">warning</option><option value="critical">critical</option>
      </select>
      <p className="sw-hint mt-2">Only continue if all of these match.</p>
      {route.filters.map((f, i) => {
        const field = fieldOf(f.field)
        const ops = opsFor(field)
        return (
          <div className="ar-row" key={i}>
            <select aria-label={`Filter ${i + 1} field`} className="form-select" value={f.field}
              onChange={e => { const nf = fieldOf(e.target.value); const op = opsFor(nf)[0]; setRow(i, { field: e.target.value, op, value: defaultFilterValue(nf, op) }) }}>
              {fields.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
            </select>
            <select aria-label={`Filter ${i + 1} operator`} className="form-select" value={f.op} onChange={e => setRow(i, { op: e.target.value, value: defaultFilterValue(field, e.target.value) })}>
              {ops.map(o => <option key={o} value={o}>{OP_WORDS[o] || o}</option>)}
            </select>
            <ValueInput key={`${f.field}-${f.op}`} id={`ar-f-${i}`} label={`Filter ${i + 1} value`} field={field} op={f.op} value={f.value} onChange={v => setRow(i, { value: v })} />
            <button type="button" className="btn btn-sm sw-btn-outline-danger" onClick={() => set({ filters: route.filters.filter((_, j) => j !== i) })}>Remove</button>
          </div>
        )
      })}
      {route.filters.length < 10 && fields.length > 0 &&
        <button type="button" className="btn sw-btn btn-sm"
          onClick={() => { const op = opsFor(fields[0])[0]; set({ filters: [...route.filters, { field: fields[0].key, op, value: defaultFilterValue(fields[0], op) }] }) }}>Add filter</button>}
      {fields.length === 0 && <p className="sw-hint">These alerts have no field in common to filter on. Pick specific alerts to filter on their fields.</p>}
    </>
  )
}

export const WhenStep = ({ route, set }) => {
  const s = route.schedule
  const setS = patch => set({ schedule: { ...s, ...patch } })
  const zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : ['UTC', 'Europe/London', 'America/New_York']
  return (
    <>
      <div className="form-check"><input id="ar-any" className="form-check-input" type="radio" checked={!s} onChange={() => set({ schedule: null })} />
        <label className="form-check-label" htmlFor="ar-any">Any time</label></div>
      <div className="form-check"><input id="ar-sched" className="form-check-input" type="radio" checked={Boolean(s)}
        onChange={() => set({ schedule: { timezone: viewerZone(), days: ['mon', 'tue', 'wed', 'thu', 'fri'], from: '08:00', to: '18:00', outside: 'hold', criticalBypassesSchedule: true } })} />
        <label className="form-check-label" htmlFor="ar-sched">Only during a schedule</label></div>
      {s && (
        <div className="ar-form ms-4">
          <label className="sw-label" htmlFor="ar-tz">Time zone</label>
          <input id="ar-tz" className="form-control" list="ar-tz-list" value={s.timezone} onChange={e => setS({ timezone: e.target.value })} />
          <datalist id="ar-tz-list">{zones.map(z => <option key={z} value={z} />)}</datalist>
          <div className="ar-days" role="group" aria-label="Days">{DAYS.map(([k, l]) => (
            <label key={k} className="form-check-inline"><input type="checkbox" checked={s.days.includes(k)}
              onChange={() => setS({ days: s.days.includes(k) ? s.days.filter(d => d !== k) : [...s.days, k] })} /> {l}</label>))}</div>
          <label className="sw-label" htmlFor="ar-from">From</label><input id="ar-from" type="time" className="form-control" value={s.from} onChange={e => setS({ from: e.target.value })} />
          <label className="sw-label" htmlFor="ar-to">To</label><input id="ar-to" type="time" className="form-control" value={s.to} onChange={e => setS({ to: e.target.value })} />
          <div className="form-check"><input id="ar-hold" className="form-check-input" type="radio" checked={s.outside === 'hold'} onChange={() => setS({ outside: 'hold' })} />
            <label className="form-check-label" htmlFor="ar-hold">Outside the schedule: hold until it opens</label></div>
          <div className="form-check"><input id="ar-skip" className="form-check-input" type="radio" checked={s.outside === 'skip'} onChange={() => setS({ outside: 'skip' })} />
            <label className="form-check-label" htmlFor="ar-skip">Outside the schedule: skip</label></div>
          <div className="form-check"><input id="ar-crit" className="form-check-input" type="checkbox" checked={s.criticalBypassesSchedule !== false}
            onChange={e => setS({ criticalBypassesSchedule: e.target.checked })} />
            <label className="form-check-label" htmlFor="ar-crit">Critical alerts ignore the schedule</label></div>
        </div>
      )}
      <hr />
      <div className="form-check"><input id="ar-now" className="form-check-input" type="radio" checked={route.deliveryMode !== 'digest'} onChange={() => set({ deliveryMode: 'immediate', digestMinutes: 0 })} />
        <label className="form-check-label" htmlFor="ar-now">Send now</label></div>
      <div className="form-check"><input id="ar-digest" className="form-check-input" type="radio" checked={route.deliveryMode === 'digest'} onChange={() => set({ deliveryMode: 'digest', digestMinutes: 15 })} />
        <label className="form-check-label" htmlFor="ar-digest">Collect into a digest</label></div>
      {route.deliveryMode === 'digest' && (<>
        <label className="sw-label" htmlFor="ar-every">Every</label>
        <select id="ar-every" className="form-select" value={route.digestMinutes} onChange={e => set({ digestMinutes: Number(e.target.value) })}>
          {DIGESTS.map(m => <option key={m} value={m}>{m >= 60 ? `${m / 60} h` : `${m} min`}</option>)}
        </select></>)}
    </>
  )
}

// splitConfig sends secret fields only in `secrets`, never in `config`
// (config is stored unsealed and returned to the client).
export const splitConfig = (schema, value) => {
  const secrets = {}
  const config = {}
  schema.forEach(f => { if (value[f.key] !== undefined && value[f.key] !== '') (f.secret ? secrets : config)[f.key] = value[f.key] })
  return { config, secrets }
}

// The one-time panel for a generated secret (a webhook's signing secret).
// It is drawn from the action's `pending`, which lives in the route
// editor's state, so leaving the step can't lose it; until "I've copied
// it", the editor blocks step changes, this action's target and Remove,
// and Save (spec §12.4, §20.2: shown once).
export const pendingSecret = route => (route.actions || []).some(a => a.pending)

// The pending secret's panel is the shared SecretPanel (also the target drawer's).
const PendingSecret = ({ pending, onConfirm }) => {
  const catalog = useCatalog()
  const at = catalog.actionTypes.find(x => x.type === pending.target.type)
  const labelOf = k => { const f = at && at.configSchema.find(x => x.key === k); return f ? f.label : k }
  return <div className="ar-newtarget"><SecretPanel secrets={pending.secrets} labelOf={labelOf} onConfirm={onConfirm} /></div>
}

const NewTarget = ({ type, onCreated }) => {
  const catalog = useCatalog()
  const at = catalog.actionTypes.find(a => a.type === type)
  const [name, setName] = useState('')
  const [value, setValue] = useState({})
  const [errors, setErrors] = useState({})
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  if (!at) return null
  const create = async () => {
    const e = validateSchema(at.configSchema, value, true)
    if (!name.trim()) e.name = 'Give the target a name.'
    setErrors(e)
    if (Object.keys(e).length) return
    try {
      setError(null)
      setBusy(true)
      const out = await createTarget({ type, name: name.trim(), ...splitConfig(at.configSchema, value) })
      onCreated(out.target, out.generatedSecrets && Object.keys(out.generatedSecrets).length ? out.generatedSecrets : null)
    } catch (err) {
      setError(serverMessage(err, "The target couldn't be created."), err)
      setBusy(false)
    }
  }
  return (
    <div className="ar-newtarget">
      <label className="sw-label" htmlFor="ar-tname">Target name</label>
      <input id="ar-tname" className="form-control" value={name} onChange={e => setName(e.target.value)} />
      {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
      <SchemaForm schema={at.configSchema} value={value} onChange={setValue} isNew errors={errors} />
      {hasEmailList(at) && <p className="sw-hint">Email addresses are added on the <Link to={TARGETS_TAB}>Targets tab</Link> once the target is created.</p>}
      <button type="button" className="btn sw-btn-primary btn-sm" onClick={create} disabled={busy}>Create target</button>
      {error && <p className="text-danger small" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} />}
    </div>
  )
}

// A live preview of an action's mapping, rendered from the trigger's sample.
const MappingPreview = ({ schema, mapping, trigger }) => {
  const ctx = sampleContext(trigger)
  const lines = schema.filter(m => m.kind === 'template' || m.kind === 'template_list')
  if (!lines.length) return null
  return (
    <div className="ar-card ar-live" aria-label="Live preview">
      <p className="sw-hint m-0">Live preview{trigger ? `: ${trigger.label}` : ''}</p>
      {lines.map(m => (m.kind === 'template_list'
        ? (mapping[m.key] || []).map((r, i) => <p key={`${m.key}-${i}`} className="m-0"><strong>{renderTemplate(r.label, ctx)}</strong>: {renderTemplate(r.value, ctx)}</p>)
        : <p key={m.key} className="m-0"><span className="sw-hint">{m.label}:</span> {renderTemplate(mapping[m.key], ctx)}</p>))}
    </div>
  )
}

export const ActionsStep = ({ route, set, readOnly }) => {
  const catalog = useCatalog()
  const [targets, setTargets] = useState([])
  // A target made here is listed at once, and kept when the list loads.
  const made = route.actions.map(a => (a.pending && a.pending.target) || a.madeTarget).filter(Boolean)
  useEffect(() => { listTargets().then(ts => setTargets(prev => [...ts, ...prev.filter(p => !ts.some(t => t.id === p.id))]), () => {}) }, [])
  const allTargets = [...targets, ...made.filter(m => !targets.some(t => t.id === m.id))]
  const covered = coveredTriggers(route, catalog)
  const fields = [...new Map(covered.flatMap(t => t.schema).map(f => [f.key, f])).values()]
  const defaults = (covered[0] && covered[0].mapping) || {}
  const setAction = (i, patch) => set({ actions: route.actions.map((a, j) => (j === i ? { ...a, ...patch } : a)) })
  const move = (i, d) => { const a = [...route.actions]; [a[i], a[i + d]] = [a[i + d], a[i]]; set({ actions: a }) }
  return (
    <>
      {route.actions.map((a, i) => {
        const tgt = allTargets.find(t => t.id === a.targetID)
        const locked = Boolean(a.pending)
        const at = tgt && catalog.actionTypes.find(x => x.type === tgt.type)
        const noAddresses = at && hasEmailList(at) && (tgt.addresses || []).length === 0
        return (
          <section className="ar-action" key={i} aria-label={`Action ${i + 1}`}>
            <div className="ar-row">
              <select aria-label={`Action ${i + 1} target`} className="form-select" disabled={locked} value={a.newType ? `new:${a.newType}` : a.targetID || ''}
                onChange={e => (e.target.value.startsWith('new:') ? setAction(i, { newType: e.target.value.slice(4), targetID: 0 }) : setAction(i, { targetID: Number(e.target.value), newType: null }))}>
                <option value="">Pick a target…</option>
                {allTargets.map(t => <option key={t.id} value={t.id}>{t.name} ({t.type})</option>)}
                {!readOnly && catalog.actionTypes.map(x => <option key={x.type} value={`new:${x.type}`}>New {x.label} target…</option>)}
              </select>
              <label className="form-check-inline"><input type="checkbox" aria-label={`Action ${i + 1} on`} checked={a.enabled !== false} onChange={e => setAction(i, { enabled: e.target.checked })} /> on</label>
              {i > 0 && <button type="button" className="btn btn-sm sw-btn" aria-label={`Move action ${i + 1} up`} onClick={() => move(i, -1)}>↑</button>}
              {i < route.actions.length - 1 && <button type="button" className="btn btn-sm sw-btn" aria-label={`Move action ${i + 1} down`} onClick={() => move(i, 1)}>↓</button>}
              <button type="button" className="btn btn-sm sw-btn-outline-danger" disabled={locked} onClick={() => set({ actions: route.actions.filter((_, j) => j !== i) })}>Remove</button>
            </div>
            {a.pending && <PendingSecret pending={a.pending} onConfirm={() => setAction(i, { targetID: a.pending.target.id, newType: null, pending: null, madeTarget: a.pending.target })} />}
            {a.newType && !a.pending && !readOnly && <NewTarget type={a.newType} onCreated={(t, secrets) => {
              setTargets(ts => [...ts, t])
              setAction(i, secrets ? { pending: { target: t, secrets } } : { targetID: t.id, newType: null, madeTarget: t })
            }} />}
            {noAddresses && <p className="sw-hint">This target has no email addresses yet. Add them on the <Link to={TARGETS_TAB}>Targets tab</Link>.</p>}
            {at && <MappingForm schema={at.mappingSchema} value={a.mapping || {}} defaults={defaults} fields={fields} onChange={m => setAction(i, { mapping: m })} />}
            {at && <MappingPreview schema={at.mappingSchema} mapping={effectiveMapping(at.mappingSchema, a.mapping || {}, defaults)} trigger={covered[0]} />}
          </section>
        )
      })}
      <button type="button" className="btn sw-btn btn-sm" onClick={() => set({ actions: [...route.actions, { targetID: 0, enabled: true, mapping: {} }] })}>Add action</button>
    </>
  )
}

// The headers a webhook delivery carries (spec §12.4). The server's own
// list wins when the preview sends one; the signature is made at send time.
export const webhookHeaders = a => {
  if (a.webhookHeaders) return Object.entries(a.webhookHeaders)
  const m = a.message || {}
  return [
    ['Content-Type', 'application/json'],
    ['User-Agent', 'EducationXR-Alerts/1'],
    ['X-EdXR-Delivery', m.deliveryID ? String(m.deliveryID) : '(the delivery id, set when sent)'],
    ['X-EdXR-Event', m.event || '(placeholder: triggered, digest or test, set when sent)'],
    ['Idempotency-Key', m.idempotencyKey || '(set when sent)'],
    ['X-EdXR-Signature', 't=<unix time>,v1=<HMAC-SHA256 of t.body with the signing secret, made when sent>']
  ]
}

const EmailPreview = ({ message }) => {
  const r = message.rendered || {}
  const vars = (message.email && message.email.variables) || {}
  return (
    <div className="ar-email">
      {(message.email.to || []).length > 0 && <p className="m-0"><span className="sw-hint">To:</span> {message.email.to.join(', ')}</p>}
      <p className="m-0"><span className="sw-hint">Subject:</span> {vars.title || r.title}</p>
      <div className="ar-email-body">
        <p className="m-0">{vars.body || r.body}</p>
        {(r.fields || []).length > 0 && <dl className="ar-fields">{r.fields.map((f, i) => <React.Fragment key={i}><dt>{f.label}</dt><dd>{f.value}</dd></React.Fragment>)}</dl>}
        {r.link && <p className="m-0"><span className="sw-hint">Link:</span> {r.link}</p>}
      </div>
      <p className="sw-hint m-0">Email design: {message.email.templateKey}</p>
    </div>
  )
}

// A filter's result in words: "Events is at least 20: saw 25 · matches".
const RESULT_WORDS = { ...OP_WORDS, gte: 'is at least', lte: 'is at most' }
export const plainValue = v => {
  if (v === undefined || v === null || v === '') return 'nothing'
  if (Array.isArray(v)) return v.length ? v.map(plainValue).join(', ') : 'nothing'
  if (v === true) return 'yes'
  if (v === false) return 'no'
  if (typeof v === 'object') return Object.values(v).map(plainValue).join(', ')
  return String(v)
}
export const filterResult = (f, label) => {
  const name = label(f.field)
  const rule = f.op === 'exists' ? `${name} ${f.want === false ? 'is not set' : 'is set'}` : `${name} ${RESULT_WORDS[f.op] || f.op} ${plainValue(f.want)}`
  return `${rule}: saw ${plainValue(f.saw)} · ${f.pass ? 'matches' : "doesn't match"}`
}

export const TestStep = ({ route, body, savedID, dirty, readOnly }) => {
  const catalog = useCatalog()
  const covered = coveredTriggers(route, catalog)
  const [trigger, setTrigger] = useState('')
  const [preview, setPreview] = useState(null)
  const [run, setRun] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const kind = trigger || (covered[0] && covered[0].kind) || ''
  const fieldLabel = key => {
    const f = covered.flatMap(t => t.schema).find(x => x.key === key)
    return f ? f.label : key
  }
  const doPreview = async () => {
    try { setError(null); setPreview(await previewRoute({ route: body, trigger: kind })) } catch (err) { setError(serverMessage(err, "The preview couldn't be made."), err) }
  }
  const send = async () => {
    try { setError(null); setRun((await testRoute(savedID, { trigger: kind, send: true })).run) } catch (err) { setError(serverMessage(err, "The test couldn't be sent."), err) }
  }
  return (
    <>
      <label className="sw-label" htmlFor="ar-test-trigger">Alert to test with</label>
      <select id="ar-test-trigger" className="form-select" value={kind} onChange={e => setTrigger(e.target.value)}>
        {covered.map(t => <option key={t.kind} value={t.kind}>{t.label}</option>)}
      </select>
      <div className="ar-row mt-2">
        <button type="button" className="btn sw-btn" onClick={doPreview}>Preview</button>
        {!readOnly && <button type="button" className="btn sw-btn-primary" disabled={!savedID || dirty} onClick={send}
          title={!savedID || dirty ? 'Save the route first; the preview works on unsaved changes' : ''}>Send test</button>}
      </div>
      {error && <p className="text-danger small" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} />}
      {preview && (
        <div className="ar-preview">
          <p>{preview.evaluation.pass ? 'This route would send.' : "This route wouldn't send."} Severity {preview.evaluation.severityPass ? 'passes' : 'is too low'}
            {preview.evaluation.scheduleActive ? '' : ' · outside its schedule now'}</p>
          <ul>{(preview.evaluation.filters || []).map((f, i) => <li key={i}>{filterResult(f, fieldLabel)}</li>)}</ul>
          {preview.actions.map((a, i) => (
            <div key={i} className="ar-card">
              <strong>{a.targetName}</strong> ({a.type}) → {(a.destinations || []).join(', ')}
              {a.error ? <p className="text-danger">{a.error}</p> : <p>{a.message.rendered.title}</p>}
              {!a.error && a.message.email && <EmailPreview message={a.message} />}
              {a.webhookBody && (
                <>
                  <p className="sw-hint m-0">Headers</p>
                  <pre className="ar-json" aria-label="Webhook headers">{webhookHeaders(a).map(([k, v]) => `${k}: ${v}`).join('\n')}</pre>
                  <p className="sw-hint m-0">Body</p>
                  <pre className="ar-json" aria-label="Webhook body">{JSON.stringify(a.webhookBody, null, 2)}</pre>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {run && (
        <ul className="ar-run">{run.deliveries.map(d => <li key={d.id}>{d.destination}: {(d.history || []).map((h, i) => <span key={i}>{h.outcome} · {h.providerStatus} </span>)}</li>)}</ul>
      )}
    </>
  )
}
